import { Tooltip, Typography, type TypographyProps } from '@mui/material';

import { useTimezoneDisplay } from './timezone';
import {
  DateTime,
  DATETIME_FULL,
  formatInTimezone,
  getTimezoneLabel,
  parseISO8601DateTime,
} from './utils/dates';

/** Which zone drives the visible cell text when a lot timezone is known. */
export type DateTimeDisplayPrimaryTimezone = 'user' | 'location';

export interface DateTimeDisplayProps<T extends boolean>
  extends Omit<TypographyProps, 'children'> {
  dateTime: DateTime<T> | Date | string;
  locationTimezone?: string;
  format?: Intl.DateTimeFormatOptions;
  /**
   * `user` (default): wall clock in the viewer's timezone; tooltip can show lot time.
   * `location`: wall clock in `locationTimezone` (permit list / ops at the lot).
   */
  primaryTimezone?: DateTimeDisplayPrimaryTimezone;
}

export function DateTimeDisplay<T extends boolean>({
  dateTime: dateTimeProp,
  locationTimezone,
  format = DATETIME_FULL,
  primaryTimezone = 'user',
  ...typographyProps
}: DateTimeDisplayProps<T>) {
  const dateTime = DateTime.isDateTime(dateTimeProp)
    ? dateTimeProp
    : dateTimeProp instanceof Date
      ? DateTime.fromJSDate(dateTimeProp)
      : parseISO8601DateTime(dateTimeProp as string);

  const { displayTime, locationTime, userTimezone } = useTimezoneDisplay(
    dateTime as DateTime<T>,
    locationTimezone
  );

  const userTimeText = displayTime.toLocaleString(format);

  const canUseLocation =
    Boolean(locationTimezone) && locationTime != null && locationTime.isValid;

  if (!canUseLocation) {
    return <Typography {...typographyProps}>{userTimeText}</Typography>;
  }

  const locationTimeText = formatInTimezone(
    dateTime as DateTime<T>,
    locationTimezone!,
    format
  );
  const locationTzLabel = getTimezoneLabel(locationTimezone!);
  const userTzLabel = getTimezoneLabel(userTimezone);
  const isSameTimezone = locationTimezone === userTimezone;

  if (primaryTimezone === 'location') {
    if (isSameTimezone) {
      return <Typography {...typographyProps}>{locationTimeText}</Typography>;
    }
    const tooltipContent = (
      <>
        <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
          Your time ({userTzLabel})
        </Typography>
        <Typography variant="caption" component="div">
          {userTimeText}
        </Typography>
      </>
    );
    return (
      <Tooltip arrow title={tooltipContent} placement="top-start">
        <Typography
          {...typographyProps}
          sx={{
            cursor: 'help',
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
            textUnderlineOffset: '2px',
            ...typographyProps.sx,
          }}
        >
          {locationTimeText}
        </Typography>
      </Tooltip>
    );
  }

  const displayText = userTimeText;

  if (isSameTimezone) {
    return <Typography {...typographyProps}>{displayText}</Typography>;
  }

  const tooltipContent = (
    <>
      <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
        Location time ({locationTzLabel})
      </Typography>
      <Typography variant="caption" component="div">
        {locationTimeText}
      </Typography>
      <Typography
        variant="caption"
        component="div"
        sx={{ mt: 0.5, fontWeight: 600 }}
      >
        Your time ({userTzLabel})
      </Typography>
      <Typography variant="caption" component="div">
        {displayText}
      </Typography>
    </>
  );

  return (
    <Tooltip arrow title={tooltipContent} placement="top-start">
      <Typography
        {...typographyProps}
        sx={{
          cursor: 'help',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '2px',
          ...typographyProps.sx,
        }}
      >
        {displayText}
      </Typography>
    </Tooltip>
  );
}
